// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/claimCitations.test.ts

/**
 * The registry claims a review comment cites, in the shape the body links with.
 *
 * Here and not in `ask-v2/review/` because ask-v2 may not import V1 Ask Puntt
 * (`no-restricted-imports`), and the citation discriminant lives there. This
 * directory is the sanctioned bridge -- `AskPunttReviewCommentBody` next door
 * reaches V1's `CommentSectionBody` the same way -- and duplicating the
 * discriminant to dodge the boundary would be two definitions of what a rule
 * citation IS.
 *
 * The mapping is where two shapes meet: the stored citation is the PRODUCT's
 * (`knowledgeRuleCommentCitationSchema`), while what the renderer needs is a
 * rule id, a link anchor, and the wording to look for.
 */

import { type TicketMessageCitation } from "@mg/schemas/src/commons/ticket";

import { type LinkableClaim } from "../../plugins/rehype-claim-citation";
import { isKnowledgeRuleCitation } from "../../routes/ask-puntt/utils/commentSourceUtils";
import { citationAnchorParam } from "../../routes/settings/routes/knowledge-database/citationAnchor";
// The prefixes only, from the one module that declares them. The rail
// (`ask-v2/rail/groupRailCitations.ts`) reads the same wire format for a
// different answer, and each side had declared its own copy -- a prefix that
// drifted on one would have sent that surface's links somewhere the other's
// still worked. The TRAVERSALS stay separate on purpose: the rail groups by the
// node an edge comes FROM, this maps the claim an edge points AT back to it,
// and neither is a superset of the other.
import { CHAT_NODE, CLAIM_NODE, MEMORY_NODE } from "../../utils/graphNodes";

/**
 * Pull the linkable claims off a comment's citations.
 *
 * Only rule citations: a file or text citation has no rule to open, and the
 * `statement` is preferred over the `quote` for the same reason the chip
 * prefers it -- the statement is the claim as a reader would say it, where the
 * quote can be a raw source cell.
 *
 * `provenanceSource` is where the CHAT and MEMORY edges are read from, and it
 * defaults to the same list. It exists because the two are not always the same
 * array: `ChatMessage` hands over `dedupeRuleCitations(...)`, which drops every
 * non-rule record by design (`commentSourceUtils.ts`) -- so the graph citations
 * carrying the provenance had already been filtered out before this function
 * saw them, and every inline link fell back to the registry. Identity and
 * numbering still come from `comment.citations`, because that is the list the
 * chips are numbered over.
 */
export function claimCitationsFor(
  comment: {
    citations?: unknown[];
  },
  provenanceSource?: readonly unknown[],
): LinkableClaim[] {
  const citations = Array.isArray(comment?.citations) ? comment.citations : [];
  // Graph citations ride in the same array as rule citations unless the caller
  // pre-filtered them out, which is what the second argument is for.
  const provenance = claimProvenanceFrom(
    Array.isArray(provenanceSource) ? provenanceSource : citations,
  );
  const out: LinkableClaim[] = [];
  // Counted over RULE citations only, and incremented for every one -- linkable
  // or not -- because that is exactly how the chips under the message are
  // numbered (`ChatMessage.tsx`). A number that skipped the unlinkable ones
  // would point at the wrong chip.
  let position = 0;
  for (const citation of citations) {
    if (!isKnowledgeRuleCitation(citation as never)) continue;
    position += 1;
    const rule = citation as {
      ruleId: string;
      sourceId?: string;
      statement?: string;
      quote?: string;
      brand?: string;
      polarity?: string;
      location?: unknown;
    };
    if (!rule.ruleId) continue;
    out.push({
      ruleId: rule.ruleId,
      sourceId: rule.sourceId,
      cite: citationAnchorParam(rule as never),
      statement: rule.statement,
      quote: rule.quote,
      brand: rule.brand,
      polarity: rule.polarity,
      index: position,
      citation: citation as TicketMessageCitation,
      ...(provenance.get(rule.ruleId) ?? {}),
    });
  }
  return out;
}

interface Provenance {
  chatId?: string;
  chatLabel?: string;
  memoryId?: string;
  memoryLabel?: string;
}

/**
 * Where each cited rule came from in this enterprise's own history.
 *
 * warhol already ships this and nothing read it. A graph citation carries its
 * `edges`, and an edge into `claim:<ruleId>` whose source is `chat:<id>` means
 * that conversation cited the rule, while `memory:<id>` means a recorded
 * memory did. Measured on the warhol-review-kdb epic (chat 6ab31f2d): four
 * memory edges, one of them onto the very rule the answer was about.
 *
 * why MEMORY beats chat when both exist: they are different records, and a
 * memory's home is the memory store. Sending a memory's link to a chat takes
 * the reader somewhere that does not contain the sentence they clicked --
 * which is what shipped first, and read as a broken link even though it
 * navigated. A claim reached only by a conversation still links to that
 * conversation; the Chats section lists it either way.
 *
 * warhol already drops memories that were EXTRACTED from a conversation
 * (`store/graph/serve.py::_worth_citing`), so the overlap this preference has
 * to settle is now the rare one: a memory recorded elsewhere about a claim a
 * chat also cited.
 */
export function claimProvenanceFrom(
  citations: unknown[],
): Map<string, Provenance> {
  const out = new Map<string, Provenance>();
  for (const citation of citations) {
    const graph = citation as { graphNodeId?: string; edges?: unknown[] };
    if (!graph?.graphNodeId || !Array.isArray(graph.edges)) continue;
    for (const raw of graph.edges) {
      const edge = raw as { src?: string; dst?: string; srcLabel?: string };
      const src = String(edge?.src ?? "");
      const dst = String(edge?.dst ?? "");
      if (!dst.startsWith(CLAIM_NODE)) continue;
      const ruleId = dst.slice(CLAIM_NODE.length);
      if (!ruleId) continue;
      const label = String(edge?.srcLabel ?? "");
      // MERGED, not replaced. Setting the whole record meant a memory
      // arriving second discarded the chat that arrived first -- and because
      // a recorded memory and the conversation about it land on the same
      // claim, that silently removed the chat category from the prose
      // entirely. Each kind keeps its own id and its own label; the renderer
      // decides which one the wording links to and which rides beside it.
      const prev = out.get(ruleId) ?? {};
      if (src.startsWith(MEMORY_NODE)) {
        // First memory wins: the traversal returns its strongest edge first.
        if (!prev.memoryId) {
          out.set(ruleId, {
            ...prev,
            memoryId: src.slice(MEMORY_NODE.length),
            memoryLabel: label,
          });
        }
      } else if (src.startsWith(CHAT_NODE) && !prev.chatId) {
        out.set(ruleId, {
          ...prev,
          chatId: src.slice(CHAT_NODE.length),
          chatLabel: label,
        });
      }
    }
  }
  return out;
}
